import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { getLeaderboardPanelPath } from "@/lib/leaderboard-nav";
import { toast } from "sonner";
import {
  Trophy,
  Medal,
  ArrowLeft,
  ArrowRight,
  CaretLeft,
  CaretRight,
  Star,
  GraduationCap,
  Users,
  House,
} from "@phosphor-icons/react";

// ── Colour tokens for the 4 mark types ──────────────────────────────────────
const MARK_STYLES = {
  referral: {
    label: "Referral",
    color: "#22c55e",        // green
    bg: "rgba(34,197,94,0.12)",
    border: "rgba(34,197,94,0.35)",
  },
  bonus: {
    label: "Bonus",
    color: "#38bdf8",        // light blue
    bg: "rgba(56,189,248,0.12)",
    border: "rgba(56,189,248,0.35)",
  },
  mkcl: {
    label: "MKCL/ERA",
    color: "#f97316",        // dark orange
    bg: "rgba(249,115,22,0.12)",
    border: "rgba(249,115,22,0.35)",
  },
  engagement: {
    label: "Engage",
    color: "#a855f7",        // dark purple
    bg: "rgba(168,85,247,0.12)",
    border: "rgba(168,85,247,0.35)",
  },
  total: {
    label: "Total",
    color: "#dc2626",        // dark red
    bg: "rgba(220,38,38,0.12)",
    border: "rgba(220,38,38,0.35)",
  },
};

function MarkBadge({ type, value }) {
  const s = MARK_STYLES[type];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border"
      style={{ color: s.color, background: s.bg, borderColor: s.border }}
    >
      {value}
      <span className="opacity-70 font-normal">{s.label}</span>
    </span>
  );
}

function RankBadge({ rank }) {
  if (rank === 1)
    return (
      <div className="w-8 h-8 rounded-full bg-yellow-500/20 border border-yellow-500/50 flex items-center justify-center">
        <Trophy className="w-4 h-4 text-yellow-400" />
      </div>
    );
  if (rank === 2)
    return (
      <div className="w-8 h-8 rounded-full bg-slate-400/20 border border-slate-400/50 flex items-center justify-center">
        <Medal className="w-4 h-4 text-slate-300" />
      </div>
    );
  if (rank === 3)
    return (
      <div className="w-8 h-8 rounded-full bg-amber-700/20 border border-amber-700/50 flex items-center justify-center">
        <Medal className="w-4 h-4 text-amber-600" />
      </div>
    );
  return (
    <div className="w-8 h-8 rounded-full bg-surface-elevated border border-border/40 flex items-center justify-center text-xs font-bold text-muted-foreground">
      {rank}
    </div>
  );
}

function PaginationBar({ page, totalPages, onPageChange, loading }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 mt-8 flex-wrap">
      <button
        onClick={() => onPageChange(1)}
        disabled={page === 1 || loading}
        className="flex items-center gap-0.5 px-3 py-2 rounded-full text-sm border border-border/50 bg-surface hover:bg-surface-elevated transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        title="First page"
      >
        <ArrowLeft className="w-3.5 h-3.5" /><ArrowLeft className="w-3.5 h-3.5 -ml-2" />
      </button>
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1 || loading}
        className="flex items-center gap-1 px-4 py-2 rounded-full text-sm border border-border/50 bg-surface hover:bg-surface-elevated transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <CaretLeft className="w-4 h-4" /> Prev
      </button>
      <div className="px-4 py-2 rounded-full bg-primary/10 border border-primary/40 text-sm font-medium text-primary">
        {page} / {totalPages}
      </div>
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page === totalPages || loading}
        className="flex items-center gap-1 px-4 py-2 rounded-full text-sm border border-border/50 bg-surface hover:bg-surface-elevated transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Next <CaretRight className="w-4 h-4" />
      </button>
      <button
        onClick={() => onPageChange(totalPages)}
        disabled={page === totalPages || loading}
        className="flex items-center gap-0.5 px-3 py-2 rounded-full text-sm border border-border/50 bg-surface hover:bg-surface-elevated transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        title="Last page"
      >
        <ArrowRight className="w-3.5 h-3.5" /><ArrowRight className="w-3.5 h-3.5 -ml-2" />
      </button>
    </div>
  );
}

export default function PublicLeaderboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [timeframe, setTimeframe] = useState("all");
  const [pageSize, setPageSize] = useState(20);
  const topRef = useRef(null);

  const fetchLeaderboard = useCallback(
    async (pg = 1) => {
      setLoading(true);
      try {
        const response = await apiClient.get("/leaderboard/public", {
          params: { timeframe, page: pg, page_size: pageSize },
        });
        const data = response.data;
        setEntries(data.entries || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
        setPage(data.page || 1);
      } catch (error) {
        toast.error("Failed to load leaderboard");
      } finally {
        setLoading(false);
      }
    },
    [timeframe, pageSize]
  );

  useEffect(() => {
    setPage(1);
    fetchLeaderboard(1);
  }, [fetchLeaderboard]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchLeaderboard(newPage);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const panelPath = getLeaderboardPanelPath(user);
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(panelPath);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/90 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="h-10 w-10 rounded-full border border-border/50 bg-surface flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-surface-elevated transition-colors"
            title="Go back"
            data-testid="leaderboard-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/feed/public" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 border border-primary/50 flex items-center justify-center">
              <img src="/favicon/favicon.ico" alt="DIIT" className="w-6 h-6 rounded-sm object-contain" />
            </div>
            <div>
              <p className="font-heading font-semibold leading-tight">DIIT Leaderboard</p>
              <p className="text-xs text-muted-foreground leading-tight">Student Rankings</p>
            </div>
          </Link>

          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <Button asChild variant="outline" className="rounded-full" size="sm">
              <Link to={panelPath} data-testid="leaderboard-panel-link">
                <House className="w-4 h-4 mr-2" />
                {user ? "Dashboard" : "Public Feed"}
              </Link>
            </Button>
            {!user && (
              <Button asChild variant="outline" className="rounded-full" size="sm">
                <Link to="/login">Login</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero */}
        <div className="text-center mb-8" ref={topRef}>
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-yellow-500/20 to-primary/20 border border-yellow-500/30 flex items-center justify-center">
            <Trophy className="w-8 h-8 text-yellow-400" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-2">Student Leaderboard</h1>
          <p className="text-muted-foreground">Rankings based on referrals, engagement, bonus & MKCL/ERA marks</p>
        </div>

        {/* Score Legend */}
        <div className="flex flex-wrap gap-2 justify-center mb-6">
          {Object.entries(MARK_STYLES).map(([key, s]) => (
            <span
              key={key}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
              style={{ color: s.color, background: s.bg, borderColor: s.border }}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
          <div className="flex gap-2 flex-wrap">
            {["all", "monthly", "weekly"].map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors capitalize ${
                  timeframe === tf
                    ? "bg-primary text-primary-foreground"
                    : "bg-surface border border-border/50 text-muted-foreground hover:text-foreground"
                }`}
              >
                {tf === "all" ? "All Time" : tf.charAt(0).toUpperCase() + tf.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{total} students</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-8 px-2 rounded-full bg-surface border border-border text-xs"
            >
              <option value={10}>10/page</option>
              <option value={20}>20/page</option>
              <option value={30}>30/page</option>
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-surface rounded-xl p-4 animate-pulse flex items-center gap-4">
                <div className="w-8 h-8 bg-surface-elevated rounded-full" />
                <div className="w-10 h-10 bg-surface-elevated rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="w-1/3 h-4 bg-surface-elevated rounded" />
                  <div className="w-2/3 h-3 bg-surface-elevated rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16">
            <GraduationCap className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">No students yet.</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {entries.map((entry) => {
                const sb = entry.score_breakdown || {};
                return (
                  <div
                    key={entry.user_id}
                    className={`bg-surface rounded-xl border overflow-hidden transition-all hover:border-primary/30 ${
                      entry.rank <= 3
                        ? "border-yellow-500/20 shadow-sm shadow-yellow-500/5"
                        : "border-border/40"
                    }`}
                  >
                    <div className="flex items-center gap-3 p-4">
                      {/* Rank */}
                      <RankBadge rank={entry.rank} />

                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-sm font-bold text-background shrink-0">
                        {entry.full_name?.charAt(0).toUpperCase()}
                      </div>

                      {/* Name & course */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{entry.full_name}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <MarkBadge type="referral" value={sb.referral_marks ?? 0} />
                          <MarkBadge type="bonus" value={sb.bonus_marks ?? 0} />
                          <MarkBadge type="mkcl" value={sb.mkcl_era_marks ?? 0} />
                          <MarkBadge type="engagement" value={sb.engagement_marks ?? 0} />
                        </div>
                      </div>

                      {/* Total */}
                      <div className="text-right shrink-0">
                        <div
                          className="font-heading text-xl font-bold"
                          style={{ color: MARK_STYLES.total.color }}
                        >
                          {entry.total_marks ?? 0}
                        </div>
                        <div
                          className="text-xs font-semibold"
                          style={{ color: MARK_STYLES.total.color, opacity: 0.7 }}
                        >
                          Total
                        </div>
                      </div>
                    </div>

                    {/* Progress bar (total marks / possible theoretical max) */}
                    {entry.rank <= 3 && (
                      <div className="h-0.5 w-full bg-gradient-to-r from-yellow-500/40 via-primary/30 to-transparent" />
                    )}
                  </div>
                );
              })}
            </div>

            <PaginationBar
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              loading={loading}
            />

            <p className="text-center text-xs text-muted-foreground mt-4">
              Showing {entries.length} of {total} students · Page {page} of {totalPages}
            </p>
          </>
        )}

        {/* Score breakdown legend */}
        <div className="mt-10 p-5 rounded-xl bg-surface border border-border/40">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-4 h-4 text-primary" />
            <h3 className="font-semibold text-sm">Scoring Formula</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500 shrink-0" />
              Referral Marks — 50 pts per referral
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-sky-400 shrink-0" />
              Bonus Marks — Admin assigned
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-orange-500 shrink-0" />
              MKCL/ERA Marks — Admin assigned (max 50)
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-purple-500 shrink-0" />
              Engagement — Post×2, Like×1, Comment×1, View×0.2
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
